/**
 * Copy for the product catalogue.
 *
 * Two separate dictionaries on purpose:
 *
 * - `CATALOGUE_COPY` is the browse experience RMs use in front of a client, so it is
 *   fully bilingual and drives the page direction.
 * - `MANAGER_COPY` is the fund-manager operational tooling (letter upload, announcement
 *   composer, manager assignment), which the business asked to keep English/LTR only —
 *   these forms send regulated communications and are used by KFH Capital staff.
 *
 * Keeping them apart stops half-translated Arabic strings accumulating for screens that
 * are never rendered in Arabic.
 */

export type CatalogueLanguage = 'en' | 'ar';

export interface CatalogueStrings {
    catalogue: string;
    intro: string;
    products: string;
    search: string;
    searchAria: string;
    clearFilters: string;
    filters: string;
    allTypes: string;
    allCurrencies: string;
    allAssetClasses: string;
    availability: string;
    allProducts: string;
    yes: string;
    no: string;
    allFunds: string;
    sortBy: string;
    sortName: string;
    sortYtd: string;
    sortNav: string;
    sortDividends: string;
    funds: string;
    portfolios: string;
    announcementsFeed: string;
    active: string;
    notAvailable: string;
    portfolioService: string;
    professionalOnly: string;
    privateFund: string;
    openEnded: string;
    closedEnded: string;
    noProducts: string;
    noPortfolios: string;
    noAnnouncements: string;
    noResultsHint: string;
    nav: string;
    ytd: string;
    mtd: string;
    dividends: string;
    distributions: string;
    distribution: string;
    pnl: string;
    returnOfCapital: string;
    distributionHistory: string;
    latestDistribution: string;
    latestDistributionDate: string;
    distributionFrequency: string;
    importantUpdate: string;
    monthly: string;
    quarterly: string;
    yearly: string;
    quarterlyReports: string;
    noReports: string;
    lettersSent: string;
    noLettersSent: string;
    performance: string;
    noPerformance: string;
    performanceSummary: string;
    portfolioInfo: string;
    noDescription: string;
    portfolioType: string;
    minimumAmount: string;
    managementOptions: string;
    managementFee: string;
    riskLevels: string;
    currencies: string;
    fundDetails: string;
    information: string;
    letters: string;
    announcements: string;
    campaigns: string;
    campaign: string;
    noCampaigns: string;
    viewCampaign: string;
    fundManagers: string;
    noManagers: string;
    type: string;
    currency: string;
    assetClass: string;
    navDate: string;
    minimumUnits: string;
    minimumHolding: string;
    minimumRedemption: string;
    subscriptionFee: string;
    redemptionFee: string;
    returnSinceInception: string;
    inceptionDate: string;
    netAssets: string;
    date: string;
    percentage: string;
    notes: string;
    noDividends: string;
    totalDistributed: string;
    totalReturnOfCapital: string;
    noLetters: string;
    open: string;
    added: string;
    by: string;
    sentOn: string;
    neverSent: string;
    loading: string;
    loadFailed: string;
    retry: string;
    viewFund: string;
    close: string;
}

export const CATALOGUE_COPY: Record<CatalogueLanguage, CatalogueStrings> = {
    en: {
        catalogue: 'Product Catalogue',
        intro: 'Currently available KFH Capital products. Open a fund for its full information, dividends, performance, letters and announcements.',
        products: 'products',
        search: 'Search by name, asset class, type or currency',
        searchAria: 'Search products',
        clearFilters: 'Clear filters',
        filters: 'Filters',
        allTypes: 'All types',
        allCurrencies: 'All currencies',
        allAssetClasses: 'All asset classes',
        availability: 'Available for orders',
        allProducts: 'All products',
        yes: 'Yes',
        no: 'No',
        allFunds: 'All',
        sortBy: 'Sort by',
        sortName: 'Name',
        sortYtd: 'YTD return',
        sortNav: 'NAV per unit',
        sortDividends: 'Dividends',
        funds: 'Funds',
        portfolios: 'Portfolio Services',
        announcementsFeed: 'Latest announcements',
        active: 'Available for orders',
        notAvailable: 'Not available for orders',
        portfolioService: 'Portfolio service',
        professionalOnly: 'Professional clients',
        privateFund: 'Private',
        openEnded: 'Open ended',
        closedEnded: 'Closed ended',
        noProducts: 'No funds match your search.',
        noPortfolios: 'No available portfolio services match your search.',
        noAnnouncements: 'No announcements have been published yet.',
        noResultsHint: 'Try a different search term or clear the filters.',
        nav: 'NAV / unit',
        ytd: 'YTD',
        mtd: 'MTD',
        dividends: 'Dividends',
        distributions: 'Distributions',
        distribution: 'Distribution',
        pnl: 'PNL',
        returnOfCapital: 'Return of capital',
        distributionHistory: 'Distribution history',
        latestDistribution: 'Latest distribution',
        latestDistributionDate: 'Distribution date',
        distributionFrequency: 'Distribution frequency',
        importantUpdate: 'Important update',
        monthly: 'Monthly',
        quarterly: 'Quarterly',
        yearly: 'Yearly',
        quarterlyReports: 'Quarterly reports',
        noReports: 'No quarterly reports have been published for this fund.',
        lettersSent: 'Letters sent',
        noLettersSent: 'No letters have been sent to RMs yet.',
        performance: 'NAV performance',
        noPerformance: 'No NAV history yet',
        performanceSummary: 'NAV moved from {from} to {to} over the last {count} published NAVs.',
        portfolioInfo: 'Portfolio information',
        noDescription: 'No description has been configured.',
        portfolioType: 'Portfolio type',
        minimumAmount: 'Minimum amount',
        managementOptions: 'Management options',
        managementFee: 'Management fee',
        riskLevels: 'Risk levels',
        currencies: 'Currencies',
        fundDetails: 'Fund details',
        information: 'Information',
        letters: 'Letters',
        announcements: 'Announcements',
        campaigns: 'Campaigns sent',
        campaign: 'Campaign',
        noCampaigns: 'No completed campaigns are tagged with this fund.',
        viewCampaign: 'View campaign',
        fundManagers: 'Fund managers',
        noManagers: 'No fund managers assigned.',
        type: 'Type',
        currency: 'Currency',
        assetClass: 'Asset class',
        navDate: 'NAV date',
        minimumUnits: 'Minimum units',
        minimumHolding: 'Minimum holding',
        minimumRedemption: 'Minimum redemption',
        subscriptionFee: 'Subscription fee',
        redemptionFee: 'Redemption fee',
        returnSinceInception: 'Return since inception',
        inceptionDate: 'Inception date',
        netAssets: 'Net assets',
        date: 'Date',
        percentage: 'Percentage',
        notes: 'Notes',
        noDividends: 'No dividends have been recorded.',
        totalDistributed: 'Total distributed',
        totalReturnOfCapital: 'Total return of capital',
        noLetters: 'No letters attached to this fund.',
        open: 'Open',
        added: 'added',
        by: 'by',
        sentOn: 'Last sent',
        neverSent: 'Not sent yet',
        loading: 'Loading…',
        loadFailed: 'Could not load this fund’s communications.',
        retry: 'Retry',
        viewFund: 'View fund details',
        close: 'Close',
    },
    ar: {
        catalogue: 'دليل المنتجات',
        intro: 'منتجات بيتك كابيتال المتاحة حالياً. افتح الصندوق للاطلاع على معلوماته والتوزيعات والأداء والخطابات والإعلانات.',
        products: 'منتج',
        search: 'ابحث بالاسم أو فئة الأصول أو النوع أو العملة',
        searchAria: 'البحث في المنتجات',
        clearFilters: 'مسح عوامل التصفية',
        filters: 'عوامل التصفية',
        allTypes: 'جميع الأنواع',
        allCurrencies: 'جميع العملات',
        allAssetClasses: 'جميع فئات الأصول',
        availability: 'متاح للطلبات',
        allProducts: 'جميع المنتجات',
        yes: 'نعم',
        no: 'لا',
        allFunds: 'الكل',
        sortBy: 'ترتيب حسب',
        sortName: 'الاسم',
        sortYtd: 'العائد منذ بداية السنة',
        sortNav: 'صافي قيمة الوحدة',
        sortDividends: 'التوزيعات',
        funds: 'الصناديق',
        portfolios: 'خدمات المحافظ',
        announcementsFeed: 'أحدث الإعلانات',
        active: 'متاح للطلبات',
        notAvailable: 'غير متاح للطلبات',
        portfolioService: 'خدمة محفظة',
        professionalOnly: 'للعملاء المحترفين',
        privateFund: 'خاص',
        openEnded: 'مفتوح',
        closedEnded: 'مغلق',
        noProducts: 'لا توجد صناديق مطابقة للبحث.',
        noPortfolios: 'لا توجد خدمات محافظ متاحة مطابقة للبحث.',
        noAnnouncements: 'لم يتم نشر أي إعلانات بعد.',
        noResultsHint: 'جرّب كلمة بحث أخرى أو امسح عوامل التصفية.',
        nav: 'صافي قيمة الوحدة',
        ytd: 'منذ بداية السنة',
        mtd: 'منذ بداية الشهر',
        dividends: 'التوزيعات',
        distributions: 'التوزيعات',
        distribution: 'توزيع',
        pnl: 'الأرباح والخسائر',
        returnOfCapital: 'استرداد رأس المال',
        distributionHistory: 'سجل التوزيعات',
        latestDistribution: 'آخر توزيع',
        latestDistributionDate: 'تاريخ آخر توزيع',
        distributionFrequency: 'دورية التوزيع',
        importantUpdate: 'تحديث مهم',
        monthly: 'شهرياً',
        quarterly: 'ربع سنوي',
        yearly: 'سنوياً',
        quarterlyReports: 'التقارير الربع سنوية',
        noReports: 'لم تُنشر تقارير ربع سنوية لهذا الصندوق.',
        lettersSent: 'الخطابات المرسلة',
        noLettersSent: 'لم يتم إرسال أي خطابات لمديري العلاقات بعد.',
        performance: 'أداء صافي قيمة الأصول',
        noPerformance: 'لا يوجد سجل لصافي قيمة الأصول',
        performanceSummary: 'تغيّرت قيمة الوحدة من {from} إلى {to} خلال آخر {count} تحديثاً.',
        portfolioInfo: 'معلومات المحفظة',
        noDescription: 'لم تتم إضافة وصف.',
        portfolioType: 'نوع المحفظة',
        minimumAmount: 'الحد الأدنى للمبلغ',
        managementOptions: 'خيارات الإدارة',
        managementFee: 'رسوم الإدارة',
        riskLevels: 'مستويات المخاطر',
        currencies: 'العملات',
        fundDetails: 'تفاصيل الصندوق',
        information: 'المعلومات',
        letters: 'الخطابات',
        announcements: 'الإعلانات',
        campaigns: 'الحملات المرسلة',
        campaign: 'حملة',
        noCampaigns: 'لا توجد حملات مكتملة مرتبطة بهذا الصندوق.',
        viewCampaign: 'عرض الحملة',
        fundManagers: 'مديرو الصندوق',
        noManagers: 'لم يتم تعيين مديرين للصندوق.',
        type: 'النوع',
        currency: 'العملة',
        assetClass: 'فئة الأصول',
        navDate: 'تاريخ صافي قيمة الأصول',
        minimumUnits: 'الحد الأدنى للوحدات',
        minimumHolding: 'الحد الأدنى للاحتفاظ',
        minimumRedemption: 'الحد الأدنى للاسترداد',
        subscriptionFee: 'رسوم الاشتراك',
        redemptionFee: 'رسوم الاسترداد',
        returnSinceInception: 'العائد منذ التأسيس',
        inceptionDate: 'تاريخ التأسيس',
        netAssets: 'صافي الأصول',
        date: 'التاريخ',
        percentage: 'النسبة',
        notes: 'ملاحظات',
        noDividends: 'لا توجد توزيعات مسجلة.',
        totalDistributed: 'إجمالي التوزيعات',
        totalReturnOfCapital: 'إجمالي استرداد رأس المال',
        noLetters: 'لا توجد خطابات مرفقة بهذا الصندوق.',
        open: 'فتح',
        added: 'أضيف',
        by: 'بواسطة',
        sentOn: 'آخر إرسال',
        neverSent: 'لم يُرسل بعد',
        loading: 'جارٍ التحميل…',
        loadFailed: 'تعذّر تحميل مراسلات هذا الصندوق.',
        retry: 'إعادة المحاولة',
        viewFund: 'عرض تفاصيل الصندوق',
        close: 'إغلاق',
    },
};

/**
 * Fund-manager tooling. English only and rendered LTR, by product decision — these
 * forms trigger regulated outbound communications and are staff-facing.
 */
export const MANAGER_COPY = {
    lettersHeading: 'Attach an RM letter',
    lettersHelp: 'Letters are stored against this fund and sent only to active RMs. Each RM sees how many of their clients have a Waiting for NAV or Done order for the fund.',
    letterTitle: 'Letter title',
    letterTitlePlaceholder: 'e.g. Q3 2026 investor update',
    file: 'File',
    fileHelp: 'PDF, Word or image, up to 10MB.',
    attach: 'Attach letter',
    uploading: 'Uploading…',
    emailRms: 'Email RMs',
    whatsappRms: 'WhatsApp RMs',
    announcementHeading: 'New RM announcement',
    announcementHelp: 'The full message is emailed to every active RM. WhatsApp sends only a short notice pointing them back to the portal.',
    emailSubject: 'Email subject',
    emailSubjectPlaceholder: 'e.g. New NAV published for KFH Capital Sukuk Fund',
    emailMessage: 'Email message',
    emailPlaceholder: 'Write the full email that all active RMs will receive',
    announcementFiles: 'Files and videos',
    announcementFilesHelp: 'Documents up to 15MB in total are attached to the email. Videos, and anything beyond that budget, are sent as download links instead — email providers reject messages over 30MB.',
    addFiles: 'Add files',
    uploadingFiles: 'Uploading…',
    willAttach: 'attached',
    willLink: 'download link',
    removeFile: 'Remove',
    attachments: 'Attachments',
    notifyUsing: 'Notify RMs using',
    emailFull: 'Email (full message)',
    whatsappShort: 'WhatsApp (short portal notice)',
    publish: 'Publish and notify RMs',
    assignManagers: 'Assign managers',
    assignManagersTitle: 'Assign fund managers',
    managerEmails: 'Fund manager emails',
    managerHelp: 'Enter the KFH Capital email of each user who holds the fund_manager portal role. Only these users can send letters and announcements for this fund.',
    managerPlaceholder: 'manager@kfhcapital.com.kw',
    cancel: 'Cancel',
    save: 'Save assignments',
    distributionsHeading: 'Record a distribution',
    distributionsHelp: 'Distributions and capital redemptions are stored against this fund and feed its published performance figures (YTD, MTD and since inception).',
    entryType: 'Entry type',
    distribution: 'Distribution',
    pnl: 'PNL',
    returnOfCapital: 'Return of capital (ROC)',
    distributionPercentage: 'Distribution percentage',
    distributionDate: 'Distribution date',
    distributionNotes: 'Notes (English, optional)',
    distributionNotesPlaceholder: 'e.g. Expected exit December 2026 following the fund extension',
    distributionNotesAr: 'Notes (Arabic, optional)',
    distributionNotesArPlaceholder: 'مثال: الخروج المتوقع في ديسمبر 2026 بعد تمديد الصندوق',
    addDistribution: 'Add distribution',
    addCapitalRedemption: 'Add return of capital',
    edit: 'Edit',
    editEntry: 'Edit distribution entry',
    delete: 'Delete',
    confirmDeleteDistribution: 'Remove this distribution?',
    confirmLetterTitle: 'Send this letter to active RMs?',
    confirmAnnouncementTitle: 'Publish this announcement?',
    confirmSend: 'Send',
    audienceUnavailable: 'Recipient count unavailable',
    whatsappNotConfigured: 'Add a template ID to config/fundCommunications first',
    whatsappConfigured: 'Sends the configured short portal notification',
    notAssigned: 'You are not an assigned fund manager for this fund.',
    deliveryFinished: 'Delivery finished',
    deliveryFailed: 'Delivery failed',
} as const;
